import { useCallback } from 'react';

import { LoyaltyInRestaurantIncentiveType } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { actions, useAppDispatch } from 'state/global-state';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';

import { useAppliedRewards } from './use-applied-reward';

/**
 * Returns a `handleRemove` callback that removes one applied reward application
 * matching the given Sanity reward id. Works for both mobile-cart and in-restaurant
 * redemption methods.
 */
export const useRemoveReward = (rewardId: string) => {
  const { loyaltyUser } = useLoyaltyUser();
  const dispatch = useAppDispatch();
  const { removeFromCart } = useCart();
  const appliedRewards = useAppliedRewards();
  const {
    inRestaurantRedemptionCart,
    removeInRestaurantRedemptionEntry,
  } = useInRestaurantRedemptionContext();

  const handleRemove = useCallback(() => {
    const reward = appliedRewards.find(r => r.rewardId === rewardId);
    if (!reward) {
      return;
    }

    if (reward.redemptionMethod === RewardRedemptionMethod.IN_RESTAURANT) {
      const inRestaurantCartEntry = inRestaurantRedemptionCart.find(
        ({ details, type }) =>
          type === LoyaltyInRestaurantIncentiveType.REWARD && details.reward._id === reward.rewardId
      );

      if (inRestaurantCartEntry) {
        removeInRestaurantRedemptionEntry(inRestaurantCartEntry);
      }
      return;
    }

    const benefitId = reward.rewardBenefitId ?? reward.loyaltyEngineId;
    if (benefitId) {
      dispatch(
        actions.loyalty.unApplyReward({
          cartId: reward.cartId,
          rewardBenefitId: benefitId,
          loyaltyUser,
        })
      );
    }

    removeFromCart({ cartId: reward.cartId });
  }, [
    dispatch,
    loyaltyUser,
    appliedRewards,
    rewardId,
    removeFromCart,
    removeInRestaurantRedemptionEntry,
    inRestaurantRedemptionCart,
  ]);

  return { handleRemove };
};
